/* Single-actuator parallel-jaw gripper. Inches/lbf internally; top view.
   A guided axial crosshead pulls two equal links, closing two guided jaws.
   Jaws remain parallel: their pivots are (+/-a,0), the crosshead is (0,-h),
   and a = gap/2 + jawOffset, h = sqrt(linkLength^2-a^2).
   Closing retracts the actuator. Its base position is a mounting decision,
   separate from this linkage geometry and the selected catalogue body size. */
(function (root) {
  'use strict';
  const VERSION = '1.0.0';
  const DEFAULTS = Object.freeze({
    openGap: 5,
    closedGap: 1.5,
    linkLength: 4,
    jawOffset: .75,
    gripForce: 15,
    resistance: 3,
    efficiency: 90,
    factor: 1.5
  });
  const ANGLE_MIN = 5;
  const ANGLE_MAX = 85;
  const DEGREES = 180 / Math.PI;
  function requireValue(condition, message) {
    if (!condition) throw new Error(message);
  }
  function prepare(input) {
    const p = {...DEFAULTS, ...input};
    for (const key of Object.keys(DEFAULTS)) {
      requireValue(typeof p[key] === 'number' && Number.isFinite(p[key]), 'Enter a finite number for ' + key + '.');
    }
    requireValue(p.openGap > p.closedGap && p.closedGap >= 0, 'The open jaw gap must be larger than the closed gap, and neither gap can be negative.');
    requireValue(p.linkLength > 0 && p.jawOffset >= 0, 'Use a positive link length and a nonnegative jaw-pivot offset.');
    requireValue(p.gripForce >= 0 && p.resistance >= 0, 'Jaw gripping force and axial drive resistance cannot be negative.');
    requireValue(p.factor >= 1, 'Use a design allowance of at least 1.');
    requireValue(p.efficiency > 0 && p.efficiency <= 100, 'Mechanical efficiency must be greater than 0 and no more than 100%.');
    const aOpen = p.openGap / 2 + p.jawOffset;
    const aClosed = p.closedGap / 2 + p.jawOffset;
    requireValue(aClosed > 0 && aOpen < p.linkLength, 'The links cannot connect at these jaw gaps. Increase link length or reduce the gaps and jaw-pivot offset.');
    const openAngle = Math.acos(aOpen / p.linkLength) * DEGREES;
    const closedAngle = Math.acos(aClosed / p.linkLength) * DEGREES;
    requireValue(openAngle >= ANGLE_MIN - 1e-9 && closedAngle <= ANGLE_MAX + 1e-9,
      'Keep the links between 5° and 85° to the jaw guides throughout travel. Adjust the jaw gaps, link length or jaw-pivot offset to stay clear of a dead center.');
    return {p, aOpen, aClosed,
      hOpen: Math.sqrt(p.linkLength * p.linkLength - aOpen * aOpen),
      hClosed: Math.sqrt(p.linkLength * p.linkLength - aClosed * aClosed)};
  }
  function at(prepared, t) {
    requireValue(typeof t === 'number' && Number.isFinite(t) && t >= 0 && t <= 1, 'Animation travel must be between 0 and 1.');
    const {p, hOpen, hClosed} = prepared;
    // Uniform axial movement gives the nonlinear jaw motion of the actual links.
    const h = hOpen + (hClosed - hOpen) * t;
    const a = Math.sqrt(Math.max(0, p.linkLength * p.linkLength - h * h));
    const gap = t === 0 ? p.openGap : t === 1 ? p.closedGap : 2 * (a - p.jawOffset);
    const gapPerActuatorTravel = 2 * h / a;
    const idealActuatorForce = p.gripForce * gapPerActuatorTravel;
    const rawForce = idealActuatorForce + p.resistance;
    const force = rawForce * p.factor / (p.efficiency / 100);
    return {
      t, gap, a, h,
      linkAngleDeg: Math.atan2(h, a) * DEGREES,
      crosshead: [0, -h],
      leftJaw: [-a, 0],
      rightJaw: [a, 0],
      leftContact: [-gap / 2, 0],
      rightContact: [gap / 2, 0],
      actuatorTravel: hClosed - h,
      closingStroke: h - hOpen,
      gapPerActuatorTravel,
      mechanicalAdvantagePerJaw: 1 / gapPerActuatorTravel,
      idealActuatorForce,
      linkTension: p.gripForce * p.linkLength / a,
      rawForce,
      force
    };
  }
  function geometry(parameters, t) {
    return at(prepare(parameters), t);
  }
  function solve(parameters) {
    const prepared = prepare(parameters);
    const samples = Array.from({length: 241}, (_, index) => at(prepared, index / 240));
    const open = samples[0];
    const closed = samples[samples.length - 1];
    return {
      version: VERSION,
      parameters: {...prepared.p},
      samples,
      stroke: prepared.hClosed - prepared.hOpen,
      jawTravel: (prepared.p.openGap - prepared.p.closedGap) / 2,
      peak: closed.force,
      peakAt: 1,
      minForce: open.force,
      minGap: prepared.p.closedGap,
      maxGap: prepared.p.openGap,
      open,
      closed
    };
  }
  const api = {version: VERSION, defaults: DEFAULTS, angleLimits: [ANGLE_MIN, ANGLE_MAX], geometry, solve};
  root.FGGripperModel = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
